import {
  Alert, AlertTitle, Box, Button, Chip, CircularProgress, Collapse,
  Link, Paper, Stack, Tab,
  Table, TableBody, TableCell,
  TableHead, TableRow, Tabs, Typography
} from "@mui/material";
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router";
import Header from "../../../components/Header";
import {
  GetStateMachineExecutionSummaryQueryVariables,
  useGetStateMachineExecutionSummaryQuery, useGetStateMachineSummaryQuery,
} from "./api/stateMachines.generated";
import CodeMirror from "@uiw/react-codemirror";
import { javascript } from "@codemirror/lang-javascript";

import "@tshepomgaga/aws-sfn-graph/index.css";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const REGION = "eu-west-2";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography component="div">{children}</Typography>
        </Box>
      )}
    </div>
  );
}

function parseDetailsMessage(detail: string) {
  const errorMessage = detail && JSON.parse(detail);
  if (errorMessage?.input) {
    try {
      errorMessage.input = JSON.parse(errorMessage.input, (key, value) => key === "trace" ? undefined : value);
    } catch (e) { /* invalid JSON, keep as-is */ }
  }
  if (errorMessage?.cause) {
    try {
      errorMessage.cause = JSON.parse(errorMessage.cause, (key, value) => key === "trace" ? undefined : value);
      try {
        errorMessage.cause.errorMessage = JSON.parse(errorMessage.cause.errorMessage, (key, value) => key === "trace" ? undefined : value);
      } catch (e) { /* invalid JSON, keep as-is */ }
    } catch (e) { /* invalid JSON, keep as-is */ }
  }
  return JSON.stringify(errorMessage, null, 2);
}

function getStatusColor(status: string): "success" | "error" | "warning" | "info" | "default" {
  switch (status) {
    case "SUCCEEDED": return "success";
    case "FAILED": return "error";
    case "TIMED_OUT": return "warning";
    case "RUNNING": return "info";
    default: return "default";
  }
}

function getConsoleUrl(executionArn: string) {
  return `https://${REGION}.console.aws.amazon.com/states/home?region=${REGION}#/executions/details/${encodeURIComponent(executionArn)}`;
}

function JsonCell({ data, label }: { data?: string | null; label: string }) {
  const [open, setOpen] = useState(false);
  if (!data) return <Typography variant="body2" color="text.disabled">—</Typography>;

  let parsed: any;
  try { parsed = JSON.parse(data); } catch { parsed = data; }

  return (
    <Box>
      <Button
        size="small"
        variant="outlined"
        onClick={() => setOpen(o => !o)}
        endIcon={open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        sx={{ fontSize: 11, py: 0.25, px: 1, minWidth: 0 }}
      >
        {label}
      </Button>
      <Collapse in={open}>
        <Box mt={1}>
          <CodeMirror
            editable={false}
            extensions={[javascript()]}
            value={JSON.stringify(parsed, null, 2)}
            style={{ fontSize: 11, maxHeight: 200, overflow: "auto" }}
          />
        </Box>
      </Collapse>
    </Box>
  );
}

function OutputCell({ data }: { data?: string | null }) {
  const [open, setOpen] = useState(false);
  if (!data) return <Typography variant="body2" color="text.disabled">—</Typography>;

  let parsed: any;
  try { parsed = JSON.parse(data); } catch { parsed = data; }

  // Count errors across all company keys
  let errorCount = 0;
  if (typeof parsed === "object" && parsed !== null) {
    for (const key of Object.keys(parsed)) {
      if (Array.isArray(parsed[key]?.ERROR)) {
        errorCount += parsed[key].ERROR.length;
      }
    }
  }

  return (
    <Box>
      <Stack direction="row" gap={0.5} alignItems="center">
        <Button
          size="small"
          variant="outlined"
          onClick={() => setOpen(o => !o)}
          endIcon={open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          sx={{ fontSize: 11, py: 0.25, px: 1, minWidth: 0 }}
        >
          output
        </Button>
        {errorCount > 0 && (
          <Chip
            label={`${errorCount} error${errorCount > 1 ? "s" : ""}`}
            color="error"
            size="small"
            sx={{ fontSize: 10 }}
          />
        )}
      </Stack>
      <Collapse in={open}>
        <Box mt={1}>
          <CodeMirror
            editable={false}
            extensions={[javascript()]}
            value={JSON.stringify(parsed, null, 2)}
            style={{ fontSize: 11, maxHeight: 200, overflow: "auto" }}
          />
        </Box>
      </Collapse>
    </Box>
  );
}

function countOutputErrors(output: string | null): number {
  if (!output) return 0;
  try {
    const parsed = JSON.parse(output);
    let count = 0;
    for (const key of Object.keys(parsed)) {
      if (Array.isArray(parsed[key]?.ERROR)) {
        count += parsed[key].ERROR.length;
      }
    }
    return count;
  } catch { return 0; }
}

function getEffectiveStatus(exec: any): { label: string; color: "success" | "error" | "warning" | "info" | "default" } {
  // transmitModelToPallas errors are routed to LogError → Succeed so Step Functions
  // shows SUCCEEDED even when models failed — detect this from the output
  if (exec.status === "SUCCEEDED") {
    const errorCount = countOutputErrors(exec.output);
    if (errorCount > 0) {
      return { label: "PARTIAL ERROR", color: "warning" };
    }
  }
  // Also handle case where Lambda has populated failedStep
  if (exec.status === "SUCCEEDED" && (exec.failedStep || exec.failureError)) {
    return { label: "PARTIAL ERROR", color: "warning" };
  }
  return { label: exec.status, color: getStatusColor(exec.status) };
}

function ChildExecutionRow({ exec, index }: { exec: any; index: number }) {
  const [eventsOpen, setEventsOpen] = useState(false);
  const [failureOpen, setFailureOpen] = useState(false);
  const hasEvents = exec.events?.length > 0;
  const outputErrorCount = countOutputErrors(exec.output);
  const hasFailure = !!exec.failedStep || !!exec.failureError || outputErrorCount > 0;

  return (
    <>
      <TableRow hover sx={hasFailure ? { backgroundColor: "rgba(211,47,47,0.04)" } : {}}>
        <TableCell>{index + 1}</TableCell>
        <TableCell>
          <Typography
            variant="body2"
            sx={{ fontFamily: "monospace", fontSize: 11, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            title={exec.name}
          >
            {exec.name}
          </Typography>
        </TableCell>
        <TableCell>
          <Stack direction="row" alignItems="center" gap={0.5} flexWrap="wrap">
            {(() => {
              const { label, color } = getEffectiveStatus(exec);
              return (
                <Chip
                  label={label}
                  color={color}
                  size="small"
                  sx={{ fontSize: 10, fontFamily: "monospace" }}
                />
              );
            })()}
          </Stack>
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontSize: 11, whiteSpace: "nowrap" }}>
            {exec.startDate ? new Date(exec.startDate).toLocaleString("en-GB") : "—"}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontSize: 11, whiteSpace: "nowrap" }}>
            {exec.stopDate ? new Date(exec.stopDate).toLocaleString("en-GB") : "—"}
          </Typography>
        </TableCell>
        <TableCell>
          <JsonCell data={exec.input} label="input" />
        </TableCell>
        <TableCell>
          <OutputCell data={exec.output} />
        </TableCell>
        <TableCell>
          <Stack direction="row" gap={0.5} flexWrap="wrap">
            {hasFailure && (
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={() => setFailureOpen(o => !o)}
                endIcon={failureOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                sx={{ fontSize: 11, py: 0.25, px: 1 }}
              >
                Error
              </Button>
            )}
            {hasEvents && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => setEventsOpen(o => !o)}
                endIcon={eventsOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                sx={{ fontSize: 11, py: 0.25, px: 1 }}
              >
                Events
              </Button>
            )}
            <Link href={getConsoleUrl(exec.executionArn)} target="_blank" rel="noopener noreferrer" underline="none">
              <Button
                size="small"
                variant="outlined"
                endIcon={<OpenInNewIcon sx={{ fontSize: 12 }} />}
                sx={{ fontSize: 11, py: 0.25, px: 1 }}
              >
                Open
              </Button>
            </Link>
          </Stack>
        </TableCell>
      </TableRow>

      {/* Failure detail row */}
      {hasFailure && (
        <TableRow>
          <TableCell colSpan={8} sx={{ py: 0, border: failureOpen ? undefined : "none" }}>
            <Collapse in={failureOpen} unmountOnExit>
              <Box sx={{ m: 1, p: 1.5, backgroundColor: "rgba(211,47,47,0.06)", borderRadius: 1, border: "1px solid rgba(211,47,47,0.2)" }}>
                <Typography variant="subtitle2" color="error" mb={1}>
                  transmitModelToPallas failures — {exec.failureError || `${outputErrorCount} model(s) failed`}
                </Typography>
                {/* Show structured errors parsed from output */}
                {(() => {
                  try {
                    const parsed = JSON.parse(exec.output || "{}");
                    const rows: React.ReactNode[] = [];
                    for (const company of Object.keys(parsed)) {
                      const errors = parsed[company]?.ERROR;
                      if (!Array.isArray(errors)) continue;
                      errors.forEach((e: any, i: number) => {
                        let msg = e.errorMessage;
                        try { msg = JSON.parse(e.errorMessage)?.message || msg; } catch { /* keep as-is */ }
                        rows.push(
                          <TableRow key={`${company}-${i}`}>
                            <TableCell sx={{ fontSize: 11, fontFamily: "monospace" }}>{company}</TableCell>
                            <TableCell sx={{ fontSize: 11, fontFamily: "monospace" }}>{e.internalId}</TableCell>
                            <TableCell sx={{ fontSize: 11, fontFamily: "monospace" }}>{e.action}</TableCell>
                            <TableCell sx={{ fontSize: 11, color: "error.main" }}>{msg}</TableCell>
                          </TableRow>
                        );
                      });
                    }
                    if (rows.length === 0) return null;
                    return (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontSize: 11, fontWeight: "bold" }}>Company</TableCell>
                            <TableCell sx={{ fontSize: 11, fontWeight: "bold" }}>Internal ID</TableCell>
                            <TableCell sx={{ fontSize: 11, fontWeight: "bold" }}>Action</TableCell>
                            <TableCell sx={{ fontSize: 11, fontWeight: "bold" }}>Error</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>{rows}</TableBody>
                      </Table>
                    );
                  } catch { return null; }
                })()}
                {/* Fallback to failureCause string if output parse failed */}
                {exec.failureCause && outputErrorCount === 0 && (
                  <Box
                    sx={{
                      p: 1, mt: 1,
                      backgroundColor: "rgba(0,0,0,0.04)",
                      borderRadius: 1,
                      fontFamily: "monospace",
                      fontSize: 11,
                      whiteSpace: "pre-wrap",
                      maxHeight: 200,
                      overflow: "auto",
                    }}
                  >
                    {exec.failureCause}
                  </Box>
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}

      {/* Events detail row */}
      {hasEvents && (
        <TableRow>
          <TableCell colSpan={8} sx={{ py: 0, border: eventsOpen ? undefined : "none" }}>
            <Collapse in={eventsOpen} unmountOnExit>
              <Box sx={{ m: 1 }}>
                <Typography variant="subtitle2" mb={1} color="text.secondary">Events</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Previous ID</TableCell>
                      <TableCell>Timestamp</TableCell>
                      <TableCell>Detail</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {exec.events.map((e: any) => (
                      <TableRow key={e.id}>
                        <TableCell>{e.id}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 11 }}>
                            {e.type}
                          </Typography>
                        </TableCell>
                        <TableCell>{e.previousEventId}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: 11, whiteSpace: "nowrap" }}>
                            {e.timestamp ? new Date(e.timestamp).toLocaleString("en-GB") : "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <JsonCell data={e.detail} label="detail" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function ChildExecutionsTable({ childExecutions }: { childExecutions: any[] }) {
  if (!childExecutions?.length) {
    return <Typography color="text.secondary">No child executions found.</Typography>;
  }

  const failureCount = childExecutions.filter(e => countOutputErrors(e.output) > 0).length;

  return (
    <>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Start Date</TableCell>
            <TableCell>Stop Date</TableCell>
            <TableCell>Input</TableCell>
            <TableCell>Output</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {childExecutions.map((exec, i) => (
            <ChildExecutionRow key={exec.executionArn} exec={exec} index={i} />
          ))}
        </TableBody>
      </Table>
    </>
  );
}

function MapRunSummary({ mapRun, childExecutions }: { mapRun: any; childExecutions?: any[] }) {
  if (!mapRun) return null;

  const transmitErrorCount = (childExecutions || []).reduce((acc: number, exec: any) => {
    return acc + countOutputErrors(exec.output);
  }, 0);

  const affectedCompanies = (childExecutions || []).filter(
    (exec: any) => countOutputErrors(exec.output) > 0
  ).length;

  return (
    <Box display="grid" gridTemplateColumns="repeat(5, 1fr)" gap={2} mb={3}>
      {[
        { label: "Processed", value: mapRun.itemsProcessed ?? "—" },
        { label: "Succeeded", value: mapRun.itemsSucceeded ?? "—", color: "success.main" },
        { label: "Failed", value: mapRun.itemsFailed ?? "—", color: "error.main" },
        { label: "Timed Out", value: mapRun.itemsTimedOut ?? "—", color: "warning.main" },
        {
          label: "Transmit Errors",
          value: transmitErrorCount > 0 ? `${transmitErrorCount} (${affectedCompanies} co.)` : "0",
          color: transmitErrorCount > 0 ? "error.main" : "text.primary",
          tooltip: transmitErrorCount > 0 ? `${transmitErrorCount} models failed across ${affectedCompanies} companies` : undefined
        },
      ].map(({ label, value, color }) => (
        <Paper key={label} variant="outlined" sx={{ p: 2, textAlign: "center" }}>
          <Typography variant="h4" fontWeight="bold" color={color ?? "text.primary"}>
            {value}
          </Typography>
          <Typography variant="body2" color="text.secondary">{label}</Typography>
        </Paper>
      ))}
    </Box>
  );
}

const StateMachineExecutionSummary = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState(1);
  const { id = "", machineId = "" } = params;

  const stateMachineExecutionParams: GetStateMachineExecutionSummaryQueryVariables = { id };

  const { data: summaryData } = useGetStateMachineSummaryQuery({ id: machineId });
  const stateMachine = summaryData?.getStateMachineSummary;

  const { data, isLoading } = useGetStateMachineExecutionSummaryQuery(stateMachineExecutionParams);
  const execution = data?.getStateMachineExecutionSummary;
  const distributedMap = execution?.distributedMap;

  if (isLoading) {
    return <CircularProgress />;
  }

  const executionSucceed = execution?.status === "SUCCEEDED";

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header title={execution ? `${execution.name}` : ""} subtitle={execution ? `${execution.id}` : ""} />
        <Button color="primary" variant="outlined" onClick={() => navigate(-1)}>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            Go Back
          </Typography>
        </Button>
      </Box>

      <Paper>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tab}
            onChange={(_, value) => setTab(value)}
            indicatorColor="secondary"
            textColor="inherit"
            variant="fullWidth"
            aria-label="full width tabs example"
          >
            <Tab label="Details" value={1} />
            <Tab label="Execution Input & Output" value={2} />
            <Tab label="Definition" value={3} />
            {distributedMap && <Tab label="Distributed Map" value={4} />}
          </Tabs>
        </Box>
        <Box mb={2}>
          <CustomTabPanel value={tab} index={1}>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1}>
              <Box>
                <Typography color="grey">Execution status:</Typography>
                {executionSucceed ? (
                  <Stack direction="row" alignItems="center" gap={0.25}>
                    <CheckCircleOutlineIcon color="success" />
                    <Typography color="green">Succeeded</Typography>
                  </Stack>
                ) : (
                  <Stack direction="row" alignItems="center" gap={0.25}>
                    <CancelOutlinedIcon color="error" />
                    <Typography color="error">Failed</Typography>
                  </Stack>
                )}
              </Box>
              <Box gridColumn="2/3">
                <Typography color="grey">Start date:</Typography>
                <Typography>{execution?.startDate}</Typography>
              </Box>
              <Box gridColumn="2/3">
                <Typography color="grey">End date:</Typography>
                <Typography>{execution?.stopDate}</Typography>
              </Box>
              <Box gridColumn="2/3">
                <Typography color="grey">Duration:</Typography>
                <Typography>{+(new Date(execution?.stopDate)) - +(new Date(execution?.startDate))}ms</Typography>
              </Box>
            </Box>
          </CustomTabPanel>

          <CustomTabPanel value={tab} index={2}>
            <Box display="grid" gridTemplateColumns="50% 50%" gap={1}>
              <Box>
                <Typography color="grey">Input:</Typography>
                <CodeMirror
                  editable={false}
                  extensions={[javascript()]}
                  value={parseDetailsMessage(execution?.input || "{}")}
                />
              </Box>
              <Box>
                <Typography color="grey">Output:</Typography>
                <CodeMirror
                  editable={false}
                  extensions={[javascript()]}
                  value={parseDetailsMessage(execution?.output || "{}")}
                />
              </Box>
            </Box>
          </CustomTabPanel>

          <CustomTabPanel value={tab} index={3}>
            <CodeMirror
              editable={false}
              extensions={[javascript()]}
              value={stateMachine?.definition || "{}"}
            />
          </CustomTabPanel>

          {distributedMap && (
            <CustomTabPanel value={tab} index={4}>
              <MapRunSummary
                mapRun={distributedMap.mapRun}
                childExecutions={distributedMap.childExecutions ?? []}
              />
              <Typography variant="h6" mb={2}>Child Executions</Typography>
              <ChildExecutionsTable childExecutions={distributedMap.childExecutions ?? []} />
            </CustomTabPanel>
          )}
        </Box>
      </Paper>

      {!executionSucceed && execution?.output && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <AlertTitle>Execution failed with error message:</AlertTitle>
          <CodeMirror
            editable={false}
            value={parseDetailsMessage(execution.output)}
            extensions={[javascript()]}
          />
        </Alert>
      )}
    </Box>
  );
};

export default StateMachineExecutionSummary;
